import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, AlertTriangle, MessageSquare, Heart, Phone, Users } from 'lucide-react';
const tools = [{
  id: 'saying-no',
  title: 'How to Say No',
  icon: Shield,
  color: 'from-red-500 to-pink-500',
  description: 'Scripts and strategies for setting boundaries confidently',
  steps: ["Be direct and clear: 'No, I'm not comfortable with that'", "You don't need to explain or justify - 'No' is a complete sentence", 'Use body language: step back, cross arms, maintain eye contact', "Repeat if needed: 'I already said no' or 'I'm not changing my mind'", "Remove yourself from the situation if they don't respect your no", "Remember: Anyone who doesn't respect your 'no' doesn't respect you"]
}, {
  id: 'reporting-abuse',
  title: 'Reporting Abuse',
  icon: AlertTriangle,
  color: 'from-orange-500 to-red-500',
  description: 'Step-by-step guide to seeking help safely',
  steps: ['Know this: Abuse is NEVER your fault. You deserve help and support.', 'Tell a trusted adult: parent, teacher, counselor, or relative', 'Call a helpline: Childline India (1098) - available 24/7', 'Document everything: dates, times, what happened (if safe to do so)', 'If in immediate danger, call 100 (police) or go to a safe place', 'You can report anonymously - your safety comes first', "Keep trying if the first person doesn't help - someone will believe you"]
}, {
  id: 'online-harassment',
  title: 'Handling Online Harassment',
  icon: MessageSquare,
  color: 'from-purple-500 to-blue-500',
  description: 'Protect yourself from cyberbullying and harassment',
  steps: ["Don't respond or engage - block and report the person immediately", 'Take screenshots as evidence (include dates and usernames)', 'Report to the platform using their abuse/harassment reporting tools', "Tell a trusted adult or friend about what's happening", 'Adjust privacy settings: make accounts private, limit who can contact you', 'Never share personal information (address, school, phone number)', 'Report to cyber crime portal: cybercrime.gov.in (India)']
}, {
  id: 'talking-to-parents',
  title: 'Talking to Parents',
  icon: Heart,
  color: 'from-pink-500 to-rose-500',
  description: 'Approach difficult conversations with adults',
  steps: ["Pick a good time when they're not busy or stressed", "Start with: 'I need to talk to you about something important'", "Be honest about your feelings: 'I'm worried/scared/confused about...'", 'If one parent is more approachable, start with them', "If your parents won't listen, try another trusted adult", 'Write a letter if talking face-to-face feels too hard', 'Remember: Good parents want to help, even if the topic is uncomfortable']
}, {
  id: 'risky-messages',
  title: 'Responding to Risky Messages',
  icon: Phone,
  color: 'from-blue-500 to-cyan-500',
  description: 'Handle uncomfortable or dangerous online interactions',
  steps: ['Trust your gut - if something feels wrong, it probably is', 'Never send intimate photos, no matter what they promise', "Don't keep secrets for adults who ask you to", 'Block immediately if they: ask for photos, want to meet alone, ask you to keep secrets', 'Tell an adult you trust right away', 'Save evidence before blocking (screenshots)', 'Remember: Adults asking teens for photos/meetings is NEVER okay']
}, {
  id: 'supporting-friends',
  title: 'Supporting Friends in Crisis',
  icon: Users,
  color: 'from-green-500 to-emerald-500',
  description: 'Help a friend while taking care of yourself',
  steps: ['Listen without judgment - let them share at their own pace', "Believe them - 'I believe you' and 'This isn't your fault' are powerful", "Don't promise to keep serious secrets (abuse, self-harm, suicide)", 'Encourage them to tell a trusted adult', 'Offer to go with them to report or get help', "Know your limits - you can't fix everything, and that's okay", 'Take care of yourself too - supporting someone is emotionally heavy']
}];
export function ToolboxPage() {
  const navigate = useNavigate();
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const tool = tools.find(t => t.id === selectedTool);
  return <div className="min-h-screen w-full bg-gradient-to-br from-orange-50 to-red-50" data-id="element-1060">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-orange-100" data-id="element-1061">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between" data-id="element-1062">
          <button onClick={() => selectedTool ? setSelectedTool(null) : navigate('/')} className="flex items-center space-x-2 text-orange-600 hover:text-orange-700" data-id="element-1063">
            <ArrowLeft className="w-5 h-5" data-id="element-1064" />
            <span className="font-medium" data-id="element-1065">Back</span>
          </button>
          <h1 className="text-lg font-bold text-gray-900" data-id="element-1066">
            Problem Solver Toolbox
          </h1>
          <div className="w-20" data-id="element-1067" />
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 py-8" data-id="element-1068">
        {!selectedTool ? <>
            <div className="text-center mb-8" data-id="element-1069">
              <h2 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-1070">
                Get Help Now
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1071">
                Step-by-step guidance for tough situations. You're not alone,
                and there are always solutions.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1072">
              {tools.map(tool => {
            const Icon = tool.icon;
            return <button key={tool.id} onClick={() => setSelectedTool(tool.id)} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all text-left group" data-id="element-1073">
                    <div className={`w-14 h-14 bg-gradient-to-br ${tool.color} rounded-xl flex items-center justify-center mb-4`} data-id="element-1074">
                      <Icon className="w-8 h-8 text-white" data-id="element-1075" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1076">
                      {tool.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4" data-id="element-1077">
                      {tool.description}
                    </p>
                    <div className="flex items-center text-sm font-semibold text-orange-600 group-hover:text-orange-700" data-id="element-1078">
                      View Guide{' '}
                      <span className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-1079">
                        →
                      </span>
                    </div>
                  </button>;
          })}
            </div>
          </> : tool ? <div className="max-w-3xl mx-auto" data-id="element-1080">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden" data-id="element-1081">
              <div className={`h-32 bg-gradient-to-br ${tool.color} flex items-center justify-center`} data-id="element-1082">
                <tool.icon className="w-16 h-16 text-white" data-id="element-1083" />
              </div>
              <div className="p-8" data-id="element-1084">
                <h2 className="text-3xl font-bold text-gray-900 mb-3" data-id="element-1085">
                  {tool.title}
                </h2>
                <p className="text-gray-600 mb-8" data-id="element-1086">{tool.description}</p>
                <div className="space-y-4" data-id="element-1087">
                  {tool.steps.map((step, index) => <div key={index} className="flex items-start space-x-4" data-id="element-1088">
                      <div className={`w-8 h-8 bg-gradient-to-br ${tool.color} rounded-full flex items-center justify-center flex-shrink-0`} data-id="element-1089">
                        <span className="text-white font-bold text-sm" data-id="element-1090">
                          {index + 1}
                        </span>
                      </div>
                      <p className="text-gray-700 pt-1" data-id="element-1091">{step}</p>
                    </div>)}
                </div>
                <div className="mt-8 p-6 bg-orange-50 border-l-4 border-orange-500 rounded" data-id="element-1092">
                  <p className="text-sm text-orange-900 font-medium" data-id="element-1093">
                    💡 Remember: If you're in immediate danger, call 100
                    (police) or Childline 1098. Your safety is the priority.
                  </p>
                </div>
              </div>
            </div>
          </div> : null}
      </div>
    </div>;
}